import { defaultLocale, Locale } from '@repo/utils'
import { IntlShape } from 'react-intl'

type DateFormatOptions = Intl.DateTimeFormatOptions & { timeZone?: string }

interface BaseProps {
  formatString?: string
  useUTC?: boolean
}

interface FormatProps extends BaseProps {
  date?: Date | string | number
}

interface FormatRangeProps extends BaseProps {
  startDate?: Date | string | number
  endDate?: Date | string | number
  useFormatter?: boolean
}

/**
 * A utility class for formatting dates.
 * @example
 * const formatter = new DateFormatter('fr-FR')
 * formatter.format({ date: new Date(), formatString: 'DD-MM-YYYY' })
 * formatter.formatDateRange({ startDate: new Date(), endDate: new Date(), formatString: 'dayMonthYear' })
 * You can also add custom templates:
 * formatter.updateTemplate('fullDate', { year: 'numeric', month: 'long', day: 'numeric' })
 * formatter.format({ date: new Date(), formatString: 'fullDate' })
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 */
export class DateFormatter {
  private locale: Locale
  private template: { [key: string]: DateFormatOptions }

  constructor(
    locale = defaultLocale,
    template: { [key: string]: DateFormatOptions } = {},
  ) {
    this.locale = locale
    this.template = template
  }

  format({ date, formatString = '', useUTC = false }: FormatProps): string {
    if (!date) {
      return '-'
    }
    const _date = new Date(date)

    const template = this.template[formatString]
    const options = template || this.parseFormatString(formatString)
    if (useUTC) {
      options.timeZone = 'UTC'
    }
    const formatter = new Intl.DateTimeFormat(this.locale, options)

    return formatter.format(_date)
  }

  formatDateRange({
    startDate,
    endDate,
    formatString = 'dayMonthYear',
    useUTC = false,
    useFormatter = true,
  }: FormatRangeProps): string {
    if (!startDate || !endDate) {
      return '-'
    }
    const _startDate = new Date(startDate)
    const _endDate = new Date(endDate)

    const template = this.template[formatString]
    const options: DateFormatOptions =
      template ||
      this.parseFormatString(formatString) ||
      this.parseFormatString('dayMonthYear')

    if (useUTC) {
      options.timeZone = 'UTC'
    }

    const formatter = new Intl.DateTimeFormat(this.locale, options)
    if (formatter.formatRange && useFormatter) {
      return formatter.formatRange(_startDate, _endDate)
    } else {
      const startFormat = this.format({
        date: _startDate,
        formatString: 'dayMonth',
        useUTC,
      })
      const endFormat = this.format({
        date: _endDate,
        formatString: 'dayMonthYear',
        useUTC,
      })
      return `${startFormat} - ${endFormat}`
    }
  }

  parseFormatString(formatString: string): DateFormatOptions {
    const options: DateFormatOptions = {}
    switch (formatString) {
      case 'MonthYear':
        options.month = 'long'
        options.year = 'numeric'
        break
      case 'dayMonth':
        options.day = 'numeric'
        options.month = 'short'
        break
      case 'dayMonthLong':
        options.day = 'numeric'
        options.month = 'long'
        break
      case 'dayMonthYear':
        options.day = 'numeric'
        options.month = 'short'
        options.year = 'numeric'
        break
      case 'fullDate':
        options.year = 'numeric'
        options.month = 'long'
        options.day = 'numeric'
        options.weekday = 'long'
        break
      default:
        if (formatString.includes('YYYY')) {
          options.year = 'numeric'
        }
        if (formatString.includes('YY')) {
          options.year = '2-digit'
        }
        if (formatString.includes('MM')) {
          options.month = '2-digit'
        }
        if (formatString.includes('M')) {
          options.month = 'numeric'
        }
        if (formatString.includes('DD')) {
          options.day = '2-digit'
        }
        if (formatString.includes('D')) {
          options.day = 'numeric'
        }
        if (formatString.includes('HH')) {
          options.hour = '2-digit'
        }
        if (formatString.includes('H')) {
          options.hour = 'numeric'
        }
        if (formatString.includes('mm')) {
          options.minute = '2-digit'
        }
        if (formatString.includes('m')) {
          options.minute = 'numeric'
        }
        if (formatString.includes('ss')) {
          options.second = '2-digit'
        }
        if (formatString.includes('s')) {
          options.second = 'numeric'
        }
    }
    return options
  }

  fromNow({ date, intl }: { date: Date; intl: IntlShape }) {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()

    const rtf = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' })

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor(diffMs / (1000 * 60))

    if (days === 1) {
      return intl.formatMessage({
        id: 'ui.DateFormatted.fromNow.yesterday',
        defaultMessage: 'Hier',
      })
    }
    if (days === 0) {
      if (hours === 0) {
        return minutes > 0
          ? rtf.format(-minutes, 'minute')
          : intl.formatMessage({
              id: 'ui.DateFormatted.fromNow.now',
              defaultMessage: 'Maintenant',
            })
      } else {
        return rtf.format(-hours, 'hour')
      }
    } else if (days < 7) {
      return new Intl.DateTimeFormat(this.locale, { weekday: 'long' }).format(
        date,
      ) // Ex: "lundi"
    } else {
      return new Intl.DateTimeFormat(this.locale, {
        day: '2-digit',
        month: '2-digit',
      }).format(date) // Ex: "04/02"
    }
  }

  updateTemplate(name: string, options: DateFormatOptions): void {
    this.template[name] = options
  }
}
